import { MaggieHttp } from '../MaggieHttp';

/**
 * File ingestion configuration entity
 */
export interface FileIngestionConfig {
  id: number;
  fileType: string;
  displayName: string;
  description?: string;
  config: IngestionConfigSchema;
  active: boolean;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Complete ingestion configuration schema
 */
/**
 * Discriminator on `IngestionConfigSchema`. `'snowflake'` configures a Snowflake-backed
 * sync via the snowflake-sync API endpoints; see `snowflakeConfig` on this schema.
 */
export type IngestionSourceType = 'file' | 'api-integration' | 'snowflake';

export interface SnowflakeIngestionConfig {
  /** FK to a `snowflakeConnection` row registered on the API. */
  connectionId: number;
  /** Bare table or view name; the runner fully-qualifies via the connection's database/schema. */
  sourceTable: string;
  /**
   * Optional — required upstream when any mapping is time-series or commentary.
   * Note: the column identifying the Foresight entity lives in
   * `entityMapping.identifierField` (shared with file ingestion).
   */
  reportingPeriodColumn?: string;
  /** Optional secondary column for (YEAR, QUARTER) tuple period shapes. */
  reportingPeriodSecondaryColumn?: string;
  /** Optional — column holding a row-level "last modified" timestamp from Snowflake. */
  sourceTimestampColumn?: string;
}

/**
 * Primary data type of a field-automation config (FD-3697). Persisted for explicitness,
 * but the runner still keys off the legacy fields (`rowStructure`, `integrationDataDomain`,
 * mapping `targetType`s) — the UI derives this on read and writes the legacy fields through
 * on save. See src/pages/Config/FieldAutomation/data-type.ts.
 */
export type FieldAutomationDataType = 'entity-properties' | 'company-financials' | 'commentary';

export interface IngestionConfigSchema {
  sourceType?: IngestionSourceType;
  dataType?: FieldAutomationDataType;
  integrationProvider?: string;
  integrationDataDomain?: string;
  snowflakeConfig?: SnowflakeIngestionConfig;
  detection?: FileDetectionConfig;
  fileLevel?: FileLevelConfig;
  entityMapping?: EntityMappingConfig;
  kpiMetadata?: KpiMetadataConfig;
  customFieldMetadata?: CustomFieldMetadataConfig;
  fieldMappings?: FieldMappingConfig[];
  integrationSync?: IntegrationSyncConfig;
  periodLengthMappings?: PeriodLengthMappingEntry[];
  scenarioMappings?: ScenarioMappingEntry[];
  upsertStrategy?: 'always' | 'only-if-empty' | 'insert-only' | 'replace-if-newer';
  // Provider budget id whose rows feed the Budget section. Every budget's rows are staged;
  // when set, sync loads only Budget rows whose staged budget id matches (others stay
  // PENDING). Omit to load all budgets. Matched against Standard Metrics' `budget_id`.
  budgetId?: string;
}

export interface PeriodLengthMappingEntry {
  source: string;
  target: string; // KpiConfigPeriods value (e.g. 'month', 'quarter', 'year', 'LTM')
}

export interface ScenarioMappingEntry {
  source: string;
  target: 'actual' | 'budget';
}

/**
 * Explicit mapping from a staging metric def to a KPI or Custom Field.
 */
export interface MetricMapping {
  stagingMetricDefId: string;
  targetType: 'kpi' | 'custom-field';
  targetId: number | null;
  periodLengthMappings?: PeriodLengthMappingEntry[];
  scenarioMappings?: ScenarioMappingEntry[];
}

/**
 * Distinct scenario and period length values queried from staging metric values for a provider.
 */
export interface StagingDistinctValues {
  scenarios: string[];
  periodLengths: string[];
}

/**
 * Staging metric definition returned by GET /integration-staging/metric-defs
 */
export interface StagingMetricDef {
  id: number;
  externalId: string;
  name: string;
  category: string;
  valueType: string;
  formatType: string;
}

/**
 * API integration sync configuration
 */
export interface IntegrationSyncConfig {
  // Explicit metric def → KPI / Custom Field mappings
  metricMappings?: MetricMapping[];

  // Global period/scenario defaults (can be overridden per MetricMapping)
  periodLengthMappings?: PeriodLengthMappingEntry[];
  scenarioMappings?: ScenarioMappingEntry[];
  upsertStrategy?: 'always' | 'only-if-empty' | 'insert-only' | 'replace-if-newer';
  // Legacy location for budgetId — promoted to the top-level schema field on load
  // (the sync reads config.budgetId first, then integrationSync.budgetId).
  budgetId?: string;

  // Legacy auto-create fields
  autoCreateDefinitions?: boolean;
  metricDefFilter?: {
    isKPI?: boolean;
    isCustomField?: boolean;
    valueTypes?: string[];
  };
  typeMapping?: TypeMappingRule[];
  periodMapping?: PeriodMappingRule[];
  groupByField?: string;
  fieldDefaults?: {
    entity?: string;
    source?: string;
    timeSeries?: boolean;
    formRefs?: string[];
    gridRefs?: string[];
    kpiSectionType?: string;
  };
}

export interface TypeMappingRule {
  valueType: string;
  formatType?: string;
  targetType: 'number' | 'string';
  renderer: { id: string; type: string; config?: Record<string, unknown> };
  formatter: { id: string; type?: string; config?: Record<string, unknown> };
}

export interface PeriodMappingRule {
  aggregationType: string;
  periods: string[];
  kpiScenario: string;
}

/**
 * File detection rules
 * Note: Required headers are automatically derived from field mappings
 */
export interface FileDetectionConfig {
  filenamePatterns?: string[];
  s3PathPatterns?: string[];
}

/**
 * File-level custom-field metadata configuration.
 * Specifies the column whose value sets the effective date of date-based (timeSeries)
 * custom-field values. Non-timeSeries custom fields ignore it (single-value behavior),
 * and leaving it unset preserves the legacy sentinel-dated writes. File runner only.
 */
export interface CustomFieldMetadataConfig {
  dateColumn?: string;
}

/**
 * KPI metadata configuration
 */
export interface KpiMetadataConfig {
  dateColumn?: string;
  periodTypeColumn?: string;
  sectionColumn?: string;
}

/**
 * File-level configuration
 */
export interface FileLevelConfig {
  provider?: string;
  sourceFormat?: 'csv' | 'excel';
  delimiter?: string;
  structureType?: 'flat' | 'hierarchical';
  rowStructure?: 'row-per-entity' | 'row-per-entity-period' | 'multiple-rows-per-entity';
  firstRowOnly?: boolean;
  validationRules?: FileValidationRules;
  kpiMetadata?: KpiMetadataConfig;
}

/**
 * File validation rules
 */
export interface FileValidationRules {
  maxSize?: number;
  minRows?: number;
  maxRows?: number;
  requiredColumns?: string[];
}

/**
 * Entity mapping configuration
 */
export interface EntityMappingConfig {
  targetEntity?: 'company' | 'round' | 'fundcompany';
  identifierField?: string;
  lookupStrategy?: 'name-lookup' | 'provider-lookup' | 'id-lookup';
  providerName?: string;
  // Round-specific fields
  dateField?: string;
  createIfNotFound?: boolean;
  dateTolerance?: number;
  // FundCompany-specific fields
  fundIdentifierField?: string;
}

/**
 * Field mapping configuration
 */
export interface FieldMappingConfig {
  sourceColumn?: string | string[];
  concatenationSeparator?: string;
  rowFilterColumn?: string; // Column name to check for row filtering (multiple-rows-per-entity mode)
  rowFilterValue?: string | number | boolean; // Value to match for row filtering
  targetType?: 'custom-field' | 'kpi' | 'entity-field' | 'commentary';
  // For commentary mappings, no destination id is needed — the entity + reporting period
  // is the natural key. Used by the Snowflake source today.
  entityField?: string;
  kpiId?: number;
  fieldConfig?: CustomFieldConfig;
  transformations?: TransformationConfig[];
  periodLengthMappings?: PeriodLengthMappingEntry[];
  scenarioMappings?: ScenarioMappingEntry[];
}

/**
 * Custom field configuration
 */
export interface CustomFieldConfig {
  name?: string;
  label?: string;
  type?: string;
}

/**
 * KPI configuration
 */
export interface KpiConfig {
  name?: string;
  label?: string;
  type?: string;
}

/**
 * Transformation configuration
 */
export interface TransformationConfig {
  type: 'direct' | 'conditional' | 'calculated';
  parameters?: Record<string, unknown>;
}

/**
 * Payload for creating a new file ingestion config
 */
export interface CreateFileIngestionConfigPayload {
  fileType: string;
  displayName: string;
  description?: string;
  config: IngestionConfigSchema;
  active?: boolean;
}

/**
 * Payload for updating a file ingestion config
 */
export interface UpdateFileIngestionConfigPayload {
  displayName?: string;
  description?: string;
  config?: IngestionConfigSchema;
  active?: boolean;
}

/**
 * Validation response
 */
export interface ValidationResponse {
  valid: boolean;
  errors?: string[];
}

/**
 * Fetch all file ingestion configs with optional active filter
 */
export async function fetchFileIngestionConfigs(active?: boolean): Promise<FileIngestionConfig[]> {
  const params = active !== undefined ? { active: active.toString() } : undefined;
  const { data } = await MaggieHttp.maggie.get<FileIngestionConfig[]>('/file-ingestion/config', { params });
  return data;
}

/**
 * Fetch a single file ingestion config by ID
 */
export async function fetchFileIngestionConfig(id: number): Promise<FileIngestionConfig> {
  const { data } = await MaggieHttp.maggie.get<FileIngestionConfig>(`/file-ingestion/config/${id}`);
  return data;
}

/**
 * Create a new file ingestion config
 */
export async function createFileIngestionConfig(
  payload: CreateFileIngestionConfigPayload
): Promise<FileIngestionConfig> {
  const { data } = await MaggieHttp.maggie.post<CreateFileIngestionConfigPayload, FileIngestionConfig>(
    '/file-ingestion/config',
    payload
  );
  return data;
}

/**
 * Update an existing file ingestion config
 */
export async function updateFileIngestionConfig(
  id: number,
  payload: UpdateFileIngestionConfigPayload
): Promise<FileIngestionConfig> {
  const { data } = await MaggieHttp.maggie.put<UpdateFileIngestionConfigPayload, FileIngestionConfig>(
    `/file-ingestion/config/${id}`,
    payload
  );
  return data;
}

/**
 * Delete (soft delete/deactivate) a file ingestion config
 */
export async function deleteFileIngestionConfig(id: number): Promise<void> {
  const { data } = await MaggieHttp.maggie.delete<void>(`/file-ingestion/config/${id}`);
  return data;
}

/**
 * Activate a file ingestion config
 */
export async function activateFileIngestionConfig(id: number): Promise<FileIngestionConfig> {
  const { data } = await MaggieHttp.maggie.post<void, FileIngestionConfig>(
    `/file-ingestion/config/${id}/activate`,
    undefined
  );
  return data;
}

/**
 * Fetch distinct scenario and period length values from staging metric values for a provider.
 * Used to populate the source dropdowns in the scenario/period length mapping editors.
 */
export async function fetchStagingDistinctValues(provider: string): Promise<StagingDistinctValues> {
  const { data } = await MaggieHttp.maggie.get<StagingDistinctValues>(
    `/integration-staging/distinct-values?provider=${encodeURIComponent(provider)}`
  );
  return data;
}

/**
 * Fetch staging metric defs for a given provider (used to populate the metric mapping dropdowns)
 */
export async function fetchStagingMetricDefs(provider: string): Promise<StagingMetricDef[]> {
  const { data } = await MaggieHttp.maggie.get<StagingMetricDef[]>(
    `/integration-staging/metric-defs?provider=${encodeURIComponent(provider)}`
  );
  return data;
}

/**
 * Validate a file ingestion config schema
 */
export async function validateFileIngestionConfig(id: number): Promise<ValidationResponse> {
  const { data } = await MaggieHttp.maggie.post<void, ValidationResponse>(
    `/file-ingestion/config/${id}/validate`,
    undefined
  );
  return data;
}
